import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import WarningIcon from '@mui/icons-material/WarningRounded';
import { Stack, Link as MuiLink, Typography } from '@mui/material';
import { capitalize } from 'es-toolkit/compat';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import VerifiedIcon from 'assets/verified.svg?react';
import { getDateFormatted, getTimeAgo } from 'shared/dates';
import { Report, UserCreated } from 'features/reports/report.types';
import { NotificationType } from 'features/notifications/notifications.constants';
import { isRingReport } from 'features/reports/reports.utils';
import {
  getResponsiveFontSize,
  withTextZoom,
} from 'theme/watchdutyTheme.utils';

type ReportMetadataProps = {
  report: Report;
  displayRelativeDate: boolean;
};

const shouldShowUserAsAnon = (user: UserCreated | null): boolean =>
  !user?.displayName;

const ICON_SIZE_PX = 16;

// Let text scale with system settings
const METADATA_FONT_SIZE = getResponsiveFontSize('0.75rem');

// Approx. 16px / 4px at default body2 size (14px); em so they scale with text
const SILENT_ICON_SIZE_EM = 1.15;
const SILENT_ICON_GAP_EM = 0.3;
const SILENT_ICON_ADVANCE_EM = SILENT_ICON_SIZE_EM + SILENT_ICON_GAP_EM;

// Negative margin gives the icon zero net width so it never wraps alone.
// The parent's padding reserves the space.
const silentNotificationIconSx = {
  fontSize: `${SILENT_ICON_SIZE_EM}em`,
  width: withTextZoom('1em'),
  height: withTextZoom('1em'),
  ml: withTextZoom(`${SILENT_ICON_GAP_EM / SILENT_ICON_SIZE_EM}em`),
  mr: withTextZoom(`-${SILENT_ICON_ADVANCE_EM / SILENT_ICON_SIZE_EM}em`),
  verticalAlign: 'text-top',
};

const criticalIndicatorSx = {
  display: 'inline-flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
  fontSize: METADATA_FONT_SIZE,
  textTransform: 'uppercase',
};

export const ReportMetadata = (props: ReportMetadataProps): JSX.Element => {
  const { report, displayRelativeDate } = props;
  const { t } = useTranslation();

  const { userCreated } = report;
  const isFromRing = isRingReport(report);

  const renderAuthorName = (): JSX.Element => {
    if (isFromRing) {
      return (
        <Typography variant="body2" sx={{ fontWeight: 'fontWeightBold' }}>
          {t('geoEvent.reports.attribution.ringUser')}
        </Typography>
      );
    }

    // The !userCreated check isn't strictly needed, but it's added to keep TS happy
    if (!userCreated || shouldShowUserAsAnon(userCreated)) {
      return (
        <Typography variant="body2" sx={{ fontWeight: 'fontWeightBold' }}>
          {t('geoEvent.reports.attribution.wdUser')}
        </Typography>
      );
    }

    const authorText = (
      <Typography variant="body2" sx={{ fontWeight: 'fontWeightBold' }}>
        {userCreated.displayName}
      </Typography>
    );

    // Default reporter: plain text, no link
    if (userCreated.isDefaultReporter) {
      return authorText;
    }

    // Has display name: link to profile
    return (
      <MuiLink
        to={`/profile/${userCreated.id}#allow-back`}
        component={Link}
        underline="none"
        color="inherit"
      >
        {authorText}
      </MuiLink>
    );
  };

  const isAnon = shouldShowUserAsAnon(userCreated);
  const headline = !isFromRing && !isAnon ? userCreated?.headline : undefined;
  const isVerified = !isFromRing && !isAnon && Boolean(userCreated?.isVerified);
  const showHeadlineRow = Boolean(headline);
  const isSilent = report.notificationType === NotificationType.SILENT;
  const isCritical = report.notificationType === NotificationType.CRITICAL;

  return (
    <Stack sx={{ width: '100%' }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ minWidth: 0 }}
      >
        {renderAuthorName()}

        {isVerified && (
          <VerifiedIcon
            width={ICON_SIZE_PX}
            height={ICON_SIZE_PX}
            style={{ flexShrink: 0 }}
            data-testid="VerifiedIcon"
            aria-label={t('geoEvent.reports.verified')}
          />
        )}
      </Stack>

      {showHeadlineRow && (
        <Typography
          variant="subtitle1"
          color="text.secondary"
          sx={{ fontSize: METADATA_FONT_SIZE, overflowWrap: 'break-word' }}
        >
          {headline}
        </Typography>
      )}

      <Typography
        variant="body2"
        color="text.primary"
        sx={{
          pt: 1,
          pr: isSilent
            ? withTextZoom(`${SILENT_ICON_ADVANCE_EM}em`)
            : undefined,
          overflowWrap: 'break-word',
        }}
      >
        {displayRelativeDate && (
          <>
            <Typography
              component="span"
              variant="body2"
              fontWeight="fontWeightBold"
            >
              {capitalize(getTimeAgo(report.dateCreated))}
            </Typography>

            <Typography component="span" variant="body2" color="text.secondary">
              {t('geoEvent.reports.separator')}
            </Typography>
          </>
        )}

        <Typography
          component="span"
          variant="body2"
          color="text.secondary"
          sx={{ pr: isCritical ? 1 : undefined }}
        >
          {getDateFormatted(report.dateCreated, {
            includeYear: !displayRelativeDate,
          })}
        </Typography>

        {isSilent && (
          <NotificationsOffIcon
            sx={silentNotificationIconSx}
            color="secondary"
          />
        )}

        {isCritical && (
          <Typography
            component="span"
            variant="subtitle1"
            color="error"
            fontWeight="fontWeightBold"
            sx={criticalIndicatorSx}
          >
            <WarningIcon
              sx={{
                mr: 0.25,
                fontSize: '1em',
                width: withTextZoom('1em'),
                height: withTextZoom('1em'),
              }}
            />
            {t('geoEvent.reports.criticalIndicator')}
          </Typography>
        )}
      </Typography>
    </Stack>
  );
};
